import {
  Dispatch,
  FunctionComponent,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from 'react';

import createSafeContext from '#/utils/createSafeContext';

interface State {
  readonly isOpen: boolean;
}

export interface Actions {
  readonly open: () => void;
  readonly close: () => void;
}

export interface View {
  readonly isOpen: boolean;
}

export interface ProviderProps {
  readonly children: ReactNode;
}

type ModalContext = [
  FunctionComponent<ProviderProps>,
  () => View,
  () => Actions,
];

/**
 * Creates a modal context.
 *
 * @param name To be used as `displayName` for the context.
 *  e.g.: `Wallet` will be used as `WalletModalView` and `WalletModalActions`.
 *
 * @returns A tuple of `[Provider, useView, useActions]`
 */
export function createModalContext(name: string): ModalContext {
  const INITIAL_STATE: State = { isOpen: false };

  const [useModalView, ViewProvider] = createSafeContext<View>(
    `${name}ModalView`,
  );

  const [useModalActions, ActionsProvider] = createSafeContext<Actions>(
    `${name}ModalActions`,
  );

  const ModalProvider = ({ children }: ProviderProps) => {
    const [state, setState] = useState(INITIAL_STATE);
    const actions = useMemo(() => createActions(setState), []);
    return (
      <ViewProvider value={state}>
        <ActionsProvider value={actions}>{children}</ActionsProvider>
      </ViewProvider>
    );
  };

  function createActions(setState: Dispatch<SetStateAction<State>>): Actions {
    return {
      open() {
        setState({ isOpen: true });
      },
      close() {
        setState({ isOpen: false });
      },
    };
  }

  return [ModalProvider, useModalView, useModalActions];
}
